import {
  API_BASE_URL,
  parseIstatecResponse,
  parseValidationError,
  type IstatecResponse,
} from './apiConfig';

/**
 * Payload for POST /submit_rfp_and_send_email.
 *
 * This endpoint accepts a multipart/form-data body (because it carries a
 * PDF file). The submission is OTP-gated the same way the contact-us
 * flow is: caller must have already obtained a fresh OTP for
 * `work_email` via /send_otp_email_validation (with
 * `require_work_email: true`) before hitting this endpoint.
 */
export interface SubmitRfpPayload {
  name: string;
  work_email: string;
  personal_email: string;
  company: string;
  phone_number: string;
  /**
   * Comma-separated list of relationship labels
   * (e.g. `"Client / Prospect, Partner / Alliance"`).
   *
   * The RFP form on the FE defaults this to the two most common values
   * mentioned on the reference UI, but the user can remove either chip
   * before submitting.
   */
  relationship: string;
  /**
   * Industry vertical selected on the RFP form (e.g. "Banking and
   * Financial Services", "Healthcare and Life Sciences"). Optional —
   * empty values are rendered as "NA" by the backend before the
   * admin email is queued.
   */
  industry?: string;
  message: string;
  otp: string;
  rfp_document: File;
}

// Kept in sync with the backend guardrails in submit_rfp_service.py. The
// FE also validates client-side so the user gets an instant error before
// wasting time uploading a bad file.
export const ALLOWED_RFP_EXTENSIONS = ['pdf'] as const;
export const MAX_RFP_BYTES = 5 * 1024 * 1024; // 5 MB
export const RFP_ACCEPT_ATTR = 'application/pdf,.pdf';

export function getRfpFileExtension(file: File): string {
  const parts = file.name.split('.');
  if (parts.length < 2) return '';
  return parts[parts.length - 1].toLowerCase();
}

export function validateRfpDocument(
  file: File | null,
): { ok: true } | { ok: false; message: string } {
  if (!file || file.size === 0) {
    return { ok: false, message: 'Please attach your RFP / RFI document.' };
  }
  if (file.size > MAX_RFP_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return {
      ok: false,
      message: `Document is ${mb} MB — max allowed is 5 MB.`,
    };
  }
  const ext = getRfpFileExtension(file);
  if (
    !ALLOWED_RFP_EXTENSIONS.includes(
      ext as (typeof ALLOWED_RFP_EXTENSIONS)[number],
    )
  ) {
    return {
      ok: false,
      message: `Only PDF files are accepted (got ".${ext || 'unknown'}").`,
    };
  }
  // MIME check — some browsers surface `application/octet-stream` for
  // uploads dragged from unusual sources, so treat "empty MIME" as OK
  // as long as the extension is `.pdf`.
  if (file.type && file.type !== 'application/pdf') {
    return {
      ok: false,
      message: `File must be a PDF (MIME type "${file.type}" not accepted).`,
    };
  }
  return { ok: true };
}

/**
 * POST /submit_rfp_and_send_email — multipart/form-data.
 * OTP is verified against `work_email`.
 */
export async function submitRfp(payload: SubmitRfpPayload): Promise<IstatecResponse> {
  try {
    const form = new FormData();
    form.append('name', payload.name);
    form.append('work_email', payload.work_email);
    form.append('personal_email', payload.personal_email);
    form.append('company', payload.company);
    form.append('phone_number', payload.phone_number);
    form.append('relationship', payload.relationship);
    form.append('industry', payload.industry ?? '');
    form.append('message', payload.message);
    form.append('otp', payload.otp);
    form.append('rfp_document', payload.rfp_document, payload.rfp_document.name);

    const res = await fetch(`${API_BASE_URL}/submit_rfp_and_send_email`, {
      method: 'POST',
      body: form,
    });
    if (res.status === 422) return parseValidationError(res);
    return parseIstatecResponse(res);
  } catch {
    return {
      status: 'error',
      message: 'Network error. Please check your connection and try again.',
      status_code: 0,
      data: null,
      request_id: '',
    };
  }
}
